import { Lexer, type Token, type Tokens } from 'marked';
import { Fragment, useMemo, type ReactNode } from 'react';
import { UiLink } from '../labels';
import { cn } from '../lib/cn';

/**
 * Markdown of the app itself (guides), rendered as React elements: no HTML is passed through,
 * raw HTML in the source shows as text, images are left out and links only lead to app paths,
 * http(s) and mailto.
 */
export function Markdown({ source, className }: { source: string; className?: string }) {
  const tokens = useMemo(() => Lexer.lex(source), [source]);
  const ids = headingIds(tokens);
  return (
    <div className={cn('flex max-w-3xl min-w-0 flex-col gap-4 text-fg', className)}>
      {blocks(tokens, ids)}
    </div>
  );
}

export interface MarkdownSection {
  /** Anchor of the heading, empty for the text before the first heading. */
  id: string;
  title: string;
  depth: number;
  /** Plain text of the section, for search. */
  text: string;
}

/** The text split at its headings (levels 2 and 3), e.g. for a table of contents or a search. */
export function markdownSections(source: string): MarkdownSection[] {
  const tokens = Lexer.lex(source);
  const ids = headingIds(tokens);
  const sections: MarkdownSection[] = [{ id: '', title: '', depth: 1, text: '' }];
  for (const token of tokens) {
    if (token.type === 'heading' && (token.depth === 2 || token.depth === 3)) {
      const heading = token as Tokens.Heading;
      sections.push({
        id: ids.get(heading)!,
        title: plain(heading.tokens),
        depth: heading.depth,
        text: '',
      });
    } else {
      const section = sections.at(-1)!;
      section.text = `${section.text} ${plainBlock(token)}`.trim();
    }
  }
  return sections.filter((section) => section.id || section.text);
}

/** Stable anchors from the heading text, unique within the document. */
function headingIds(tokens: Token[]): Map<Tokens.Heading, string> {
  const ids = new Map<Tokens.Heading, string>();
  const used = new Set<string>();
  for (const token of tokens) {
    if (token.type !== 'heading') continue;
    const heading = token as Tokens.Heading;
    const base = slug(plain(heading.tokens)) || 'section';
    let id = base;
    for (let n = 2; used.has(id); n++) id = `${base}-${n}`;
    used.add(id);
    ids.set(heading, id);
  }
  return ids;
}

const UMLAUTS: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[äöüß]/g, (c) => UMLAUTS[c]!)
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function plain(tokens: readonly Token[] | undefined): string {
  return (tokens ?? [])
    .map((token) => {
      if ('tokens' in token && token.tokens) return plain(token.tokens);
      if (token.type === 'image' || token.type === 'br') return ' ';
      return 'text' in token && typeof token.text === 'string' ? token.text : '';
    })
    .join('');
}

function plainBlock(token: Token): string {
  switch (token.type) {
    case 'list':
      return (token as Tokens.List).items.map((item) => plain(item.tokens)).join(' ');
    case 'table': {
      const table = token as Tokens.Table;
      return [table.header, ...table.rows]
        .flatMap((row) => row.map((cell) => plain(cell.tokens)))
        .join(' ');
    }
    case 'code':
      return (token as Tokens.Code).text;
    case 'space':
    case 'hr':
      return '';
    default:
      return 'tokens' in token ? plain(token.tokens) : '';
  }
}

function blocks(tokens: readonly Token[], ids: Map<Tokens.Heading, string>): ReactNode[] {
  return tokens.map((token, index) => <Fragment key={index}>{block(token, ids)}</Fragment>);
}

function block(token: Token, ids: Map<Tokens.Heading, string>): ReactNode {
  switch (token.type) {
    case 'heading': {
      const heading = token as Tokens.Heading;
      const id = ids.get(heading);
      const children = inline(heading.tokens);
      // The page title is the h1; headings inside a document start one level below.
      if (heading.depth <= 2) {
        return (
          <h2 id={id} className="mt-4 scroll-mt-20 text-lg font-semibold text-fg">
            {children}
          </h2>
        );
      }
      if (heading.depth === 3) {
        return (
          <h3 id={id} className="mt-2 scroll-mt-20 text-base font-semibold text-fg">
            {children}
          </h3>
        );
      }
      return (
        <h4 id={id} className="scroll-mt-20 font-semibold text-fg">
          {children}
        </h4>
      );
    }
    case 'paragraph':
      return <p className="leading-relaxed">{inline((token as Tokens.Paragraph).tokens)}</p>;
    case 'list': {
      const list = token as Tokens.List;
      const items = list.items.map((item, index) => (
        <li key={index} className="pl-1">
          {item.task && (
            <span aria-hidden className="mr-1.5 text-fg-muted">
              {item.checked ? '☑' : '☐'}
            </span>
          )}
          {listItem(item, ids)}
        </li>
      ));
      return list.ordered ? (
        <ol
          start={list.start === '' ? undefined : list.start}
          className="flex list-decimal flex-col gap-1.5 pl-6 marker:text-fg-muted"
        >
          {items}
        </ol>
      ) : (
        <ul className="flex list-disc flex-col gap-1.5 pl-6 marker:text-fg-subtle">{items}</ul>
      );
    }
    case 'blockquote':
      return (
        <aside className="flex flex-col gap-2 rounded-md border border-info-line bg-info-subtle px-4 py-3">
          {blocks((token as Tokens.Blockquote).tokens, ids)}
        </aside>
      );
    case 'code':
      return (
        <pre className="overflow-x-auto rounded-md border border-line bg-sunken px-4 py-3 font-mono text-sm">
          <code>{(token as Tokens.Code).text}</code>
        </pre>
      );
    case 'table':
      return <Table table={token as Tokens.Table} />;
    case 'hr':
      return <hr className="border-line" />;
    case 'space':
      return null;
    case 'html':
      return <p className="leading-relaxed">{(token as Tokens.HTML).text}</p>;
    default:
      return 'tokens' in token && token.tokens ? (
        <p className="leading-relaxed">{inline(token.tokens)}</p>
      ) : null;
  }
}

/** Tight list items hold text tokens, loose ones paragraphs. */
function listItem(item: Tokens.ListItem, ids: Map<Tokens.Heading, string>): ReactNode {
  return item.tokens.map((token, index) => (
    <Fragment key={index}>
      {token.type === 'text' ? inline((token as Tokens.Text).tokens ?? [token]) : block(token, ids)}
    </Fragment>
  ));
}

function Table({ table }: { table: Tokens.Table }) {
  const align = (value: Tokens.TableCell['align']) =>
    value === 'right' ? 'text-right' : value === 'center' ? 'text-center' : 'text-left';
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-canvas">
          <tr>
            {table.header.map((cell, index) => (
              <th
                key={index}
                scope="col"
                className={cn(
                  'border-b border-line px-3 py-2 font-medium text-fg-muted',
                  align(cell.align),
                )}
              >
                {inline(cell.tokens)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, index) => (
            <tr key={index} className="border-b border-line last:border-0">
              {row.map((cell, column) => (
                <td key={column} className={cn('px-3 py-2 align-top', align(cell.align))}>
                  {inline(cell.tokens)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function inline(tokens: readonly Token[]): ReactNode[] {
  return tokens.map((token, index) => <Fragment key={index}>{inlineToken(token)}</Fragment>);
}

function inlineToken(token: Token): ReactNode {
  switch (token.type) {
    case 'strong':
      return <strong className="font-semibold">{inline((token as Tokens.Strong).tokens)}</strong>;
    case 'em':
      return <em>{inline((token as Tokens.Em).tokens)}</em>;
    case 'del':
      return <del>{inline((token as Tokens.Del).tokens)}</del>;
    case 'codespan':
      return (
        <code className="rounded-sm bg-sunken px-1 py-0.5 font-mono text-[0.9em]">
          {(token as Tokens.Codespan).text}
        </code>
      );
    case 'br':
      return <br />;
    case 'link':
      return <MarkdownLink link={token as Tokens.Link} />;
    case 'image':
      return (token as Tokens.Image).text;
    case 'text': {
      const text = token as Tokens.Text;
      return text.tokens ? inline(text.tokens) : text.text;
    }
    case 'escape':
    case 'html':
      return (token as Tokens.Escape).text;
    default:
      return 'tokens' in token && token.tokens ? inline(token.tokens) : token.raw;
  }
}

const LINK_CLASS = 'font-medium text-accent underline underline-offset-2 hover:no-underline';

function MarkdownLink({ link }: { link: Tokens.Link }) {
  const children = inline(link.tokens);
  const href = link.href.trim();
  if (href.startsWith('#') || (href.startsWith('/') && !href.startsWith('//'))) {
    return href.startsWith('#') ? (
      <a href={href} className={LINK_CLASS}>
        {children}
      </a>
    ) : (
      <UiLink href={href} className={LINK_CLASS}>
        {children}
      </UiLink>
    );
  }
  if (/^(https?:|mailto:)/i.test(href)) {
    return (
      <a href={href} className={LINK_CLASS} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return <>{children}</>;
}
